import { computed, observable, action } from 'mobx';
import * as validator from 'validator';

import request from '../common/utils/Request';

import { InputHighlight, UserType } from '../types';

class SignUpStore {
  private static _instance: SignUpStore;

  @observable public awaitServer: boolean = false;
  @observable public confirm: string = '';
  @observable public confirmBlurred: boolean = false;
  @observable public email: string = '';
  @observable public emailBlurred: boolean = false;
  @observable public firstName: string = '';
  @observable public firstNameBlurred: boolean = false;
  @observable public lastName: string = '';
  @observable public lastNameBlurred: boolean = false;
  @observable public password: string = '';
  @observable public passwordBlurred: boolean = false;
  @observable public signUpFailed: boolean = false;
  @observable public signUpSucceeded: boolean = false;
  @observable public userType: UserType = UserType.MENTEE;

  /**
   * Use SignUpStore.instance to retrieve an instance of this class.
   */
  private constructor() { }

  /**
   * Instance of SignUpStore.
   */
  public static get instance() {
    if (!this._instance) {
      this._instance = new SignUpStore();
    }

    return this._instance;
  }

  /**
   * Check if confirm password is in valid format.
   */
  @computed
  public get isConfirmValid(): boolean {
    return !this.confirmBlurred ? true : this.confirm === this.password;
  }

  /**
   * Get the help message for confirm password if necessary.
   */
  @computed
  public get confirmHelpMessage(): string {
    return this.isConfirmValid ? '' : 'Passwords must match';
  }
  
  /**
   * Get the proper highlight colour for the confirm password field.
   */
  @computed
  public get confirmHighlight(): InputHighlight {
    return this.isConfirmValid ? InputHighlight.INFO : InputHighlight.DANGER;
  }

  /**
   * Check if email is in valid format.
   */
  @computed
  public get isEmailValid(): boolean {
    return !this.emailBlurred ? true : validator.isEmail(this.email) && !this.signUpFailed;
  }

  /**
   * Get the help message for email if necessary.
   */
  @computed
  public get emailHelpMessage(): string {
    return this.isEmailValid ? '' : 'Please enter a valid e-mail address';
  }
  
  /**
   * Get the proper highlight colour for the email field.
   */
  @computed
  public get emailHighlight(): InputHighlight {
    return this.isEmailValid ? InputHighlight.INFO : InputHighlight.DANGER;
  }

  /**
   * Check if first name is in valid format.
   */
  @computed
  public get isFirstNameValid(): boolean {
    return !this.firstNameBlurred ? true : !validator.isEmpty(this.firstName) && !this.signUpFailed;
  }

  /**
   * Get the help message for first name if necessary.
   */
  @computed
  public get firstNameHelpMessage(): string {
    return this.isFirstNameValid ? '' : 'Please enter your first name';
  }
  
  /**
   * Get the proper highlight colour for the first name field.
   */
  @computed
  public get firstNameHighlight(): InputHighlight {
    return this.isFirstNameValid ? InputHighlight.INFO : InputHighlight.DANGER;
  }
  
  /**
   * Check if last name is in valid format.
   */
  @computed
  public get isLastNameValid(): boolean {
    return !this.lastNameBlurred ? true : !validator.isEmpty(this.lastName) && !this.signUpFailed;
  }

  /**
   * Get the help message for last name if necessary.
   */
  @computed
  public get lastNameHelpMessage(): string {
    return this.isLastNameValid ? '' : 'Please enter your last name';
  }
  
  /**
   * Get the proper highlight colour for the last name field.
   */
  @computed
  public get lastNameHighlight(): InputHighlight {
    return this.isLastNameValid ? InputHighlight.INFO : InputHighlight.DANGER;
  }

  /**
   * Check if password is in valid format.
   */
  @computed
  public get isPasswordValid(): boolean {
    return !this.passwordBlurred ? true : this.password.length >= 8 && !this.signUpFailed;
  }

  /**
   * Get the help message for password if necessary.
   */
  @computed
  public get passwordHelpMessage(): string {
    let message = '';
    if (this.signUpFailed) {
      message = 'The password you entered is incorrect';
    } else if (!this.isPasswordValid) {
      message = 'Password must be between 8 and 64-character long';
    }

    return message;
  }

  /**
   * Get the proper highlight colour for the password field.
   */
  @computed
  public get passwordHighlight(): InputHighlight {
    return this.isPasswordValid ? InputHighlight.INFO : InputHighlight.DANGER;
  }

  /**
   * Check all fields on the page and see if we can submit now.
   */
  @computed
  public get isReadyForSubmit(): boolean {
    return this.userType === UserType.MENTOR
        ? this.isConfirmValid && this.isPasswordValid
        : this.isConfirmValid && this.isEmailValid && this.isFirstNameValid && this.isLastNameValid && this.isPasswordValid;
  }

  /**
   * Send request to server and create a new account.
   */
  @action
  public signUp = async() => {
    const jsonData: any = { password: this.password };
    if (this.userType === UserType.MENTEE) { 
      // TODO: Remove `username` from `jsonData` after back-end got rid of it and replace it with `email`
      jsonData.email = this.email,
      jsonData.first_name = this.firstName,
      jsonData.last_name = this.lastName,
      jsonData.password = this.password,
      jsonData.username = this.email
    }

    this.awaitServer = true;
    await request.post('/auth/users/create/', jsonData);
  }
}

export default SignUpStore;
export const signUpStore = SignUpStore.instance;


// WEBPACK FOOTER //
// ./src/stores/SignUpStore.ts